// ReceiptAttachmentsModal — large image viewer for the receipt photo
// gallery. Mirrors the legacy `attachmentsModal` flow in
// `covd_receipt.php` (main image + thumbnails + prev/next + zoom) but
// re-implemented as a portal-based modal using only first-party event
// handlers — no extra npm dependency.
//
// Behaviour summary:
//   - Main image scales to fit the container (object-fit: contain).
//   - Mouse wheel + double-click zoom in/out, click + drag pans when
//     zoomed > 1×.
//   - Thumbnail strip below the main image. Hidden when only one photo.
//   - Prev / Next arrows overlaid on the main image; wraps around.
//   - Download button uses the presigned URL (server-issued) directly.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { ChevronLeft, ChevronRight, Download, RotateCcw } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { useReceiptPhotos } from '../hooks/useReceiptPhotos';
import type { ReceiptPhotoDto } from '../../../types/api';
import styles from './ReceiptAttachmentsModal.module.css';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
const DOUBLE_CLICK_ZOOM = 2;

export interface ReceiptAttachmentsModalProps {
  open: boolean;
  encId: string | null;
  /** Optional pre-loaded photos — when provided the modal skips the
   *  network fetch and uses these instead. The detail page already has
   *  the photo set in `r.photos`, so it can pass them through. */
  initialPhotos?: ReceiptPhotoDto[] | null;
  onClose: () => void;
}

interface PanState {
  x: number;
  y: number;
}

export function ReceiptAttachmentsModal({
  open,
  encId,
  initialPhotos,
  onClose,
}: ReceiptAttachmentsModalProps): JSX.Element | null {
  const fetched = useReceiptPhotos(encId, { enabled: open && !initialPhotos });
  const photos = useMemo<ReceiptPhotoDto[]>(() => {
    if (initialPhotos && initialPhotos.length > 0) return initialPhotos;
    return fetched.data ?? [];
  }, [initialPhotos, fetched.data]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<PanState>({ x: 0, y: 0 });
  const [imgLoading, setImgLoading] = useState(true);

  const dragStateRef = useRef<{ startX: number; startY: number; pan: PanState } | null>(
    null,
  );

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Reset selection + zoom each time the modal is opened.
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      resetView();
    }
  }, [open, encId, resetView]);

  // Reset zoom whenever the active photo changes so panning state from a
  // prior image does not bleed across.
  useEffect(() => {
    resetView();
    setImgLoading(true);
  }, [activeIndex, resetView]);

  if (!open) return null;
  if (photos.length === 0) {
    // Render nothing when there are no photos — matches the brief: button
    // is hidden when no photos exist, so this branch is defensive only.
    return null;
  }

  const active = photos[activeIndex] ?? photos[0]!;
  const total = photos.length;

  function clampZoom(next: number): number {
    if (next < MIN_ZOOM) return MIN_ZOOM;
    if (next > MAX_ZOOM) return MAX_ZOOM;
    return Math.round(next * 100) / 100;
  }

  function goPrev() {
    setActiveIndex((idx) => (idx - 1 + total) % total);
  }

  function goNext() {
    setActiveIndex((idx) => (idx + 1) % total);
  }

  function handleWheel(e: ReactWheelEvent<HTMLDivElement>) {
    if (e.deltaY === 0) return;
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    setZoom((z) => {
      const next = clampZoom(z + direction * ZOOM_STEP);
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function handleDoubleClick() {
    setZoom((z) => {
      const next = z > 1 ? MIN_ZOOM : DOUBLE_CLICK_ZOOM;
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function handleMouseDown(e: ReactMouseEvent<HTMLDivElement>) {
    if (zoom <= 1) return;
    e.preventDefault();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pan: { ...pan },
    };
  }

  function handleMouseMove(e: ReactMouseEvent<HTMLDivElement>) {
    const drag = dragStateRef.current;
    if (!drag) return;
    setPan({
      x: drag.pan.x + (e.clientX - drag.startX),
      y: drag.pan.y + (e.clientY - drag.startY),
    });
  }

  function handleMouseUp() {
    dragStateRef.current = null;
  }

  function handleResetZoom() {
    resetView();
  }

  const imgStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    cursor:
      zoom > 1
        ? dragStateRef.current
          ? 'grabbing'
          : 'grab'
        : 'zoom-in',
  };

  const downloadHref = active.presigned_url ?? '#';
  const filename = active.caption?.trim()
    ? `${active.caption}`
    : `receipt-photo-${activeIndex + 1}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Attachments (${total})`}
      size="lg"
      ariaLabel={undefined}
    >
      <div className={styles.body}>
        <div
          className={styles.viewer}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
        >
          {imgLoading ? <div className={styles.spinner} aria-hidden /> : null}

          {active.presigned_url ? (
            <img
              key={active.enc_id}
              src={active.presigned_url}
              alt={active.caption ?? `Receipt photo ${activeIndex + 1}`}
              className={styles.image}
              style={imgStyle}
              draggable={false}
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
            />
          ) : (
            <span className={styles.unavailable}>Preview unavailable</span>
          )}

          {total > 1 ? (
            <>
              <button
                type="button"
                className={`${styles.navBtn} ${styles.navPrev}`}
                onClick={goPrev}
                aria-label="Previous attachment"
              >
                <ChevronLeft size={20} aria-hidden />
              </button>
              <button
                type="button"
                className={`${styles.navBtn} ${styles.navNext}`}
                onClick={goNext}
                aria-label="Next attachment"
              >
                <ChevronRight size={20} aria-hidden />
              </button>
            </>
          ) : null}

          <div className={styles.toolbar}>
            <span className={styles.zoomLabel} data-testid="zoom-level">
              {zoom.toFixed(1)}×
            </span>
            <button
              type="button"
              className={styles.toolbarBtn}
              onClick={handleResetZoom}
              aria-label="Reset zoom"
              disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
            >
              <RotateCcw size={14} aria-hidden />
            </button>
            <a
              href={downloadHref}
              download={filename}
              className={styles.toolbarBtn}
              aria-label="Download attachment"
              target="_blank"
              rel="noreferrer"
            >
              <Download size={14} aria-hidden />
            </a>
          </div>
        </div>

        {total > 1 ? (
          <div
            role="tablist"
            aria-label="Attachment thumbnails"
            className={styles.thumbStrip}
          >
            {photos.map((photo, idx) => (
              <button
                key={photo.enc_id}
                type="button"
                role="tab"
                aria-selected={idx === activeIndex}
                aria-label={`Show attachment ${idx + 1}`}
                className={
                  idx === activeIndex
                    ? styles.thumbActive
                    : styles.thumb
                }
                onClick={() => setActiveIndex(idx)}
              >
                {photo.presigned_url ? (
                  <img
                    src={photo.presigned_url}
                    alt=""
                    className={styles.thumbImg}
                    loading="lazy"
                  />
                ) : (
                  <span className={styles.thumbPlaceholder}>—</span>
                )}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
